import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, AlertTriangle, Loader2, ChevronDown } from 'lucide-react';

// Calls Commander's OWN backend (the same channel the rest of the app uses).
// The backend proxies to the GauriLabs Safety engine server-to-server, so the
// browser never makes a cross-origin call — no CORS / cert / preflight / extension
// issues are possible. The API key stays server-side and is never sent to the browser.
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LEVEL = {
  GREEN:  { color: '#10B981', ring: 'border-emerald-500/40', bg: 'bg-emerald-500/10' },
  YELLOW: { color: '#eab308', ring: 'border-yellow-500/40',  bg: 'bg-yellow-500/10' },
  ORANGE: { color: '#f97316', ring: 'border-orange-500/40',  bg: 'bg-orange-500/10' },
  RED:    { color: '#ef4444', ring: 'border-red-500/40',     bg: 'bg-red-500/10' },
};

const DISCLAIMER =
  'Proceed at your own risk. GauriLabs provides risk intelligence for informational purposes ' +
  'only, this is not financial advice, and GauriLabs is not liable for any loss of funds and ' +
  'cannot be held liable for the same. By proceeding you acknowledge this token carries ' +
  'elevated rug / risk indicators.';

const isValidMint = (m) => typeof m === 'string' && m.trim().length >= 32 && m.trim().length <= 44;

export default function TokenSafetyCheck({ mint, source = 'commander', actor = '', onGate }) {
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const debounceRef = useRef(null);

  const gate = useCallback((blocked) => { if (typeof onGate === 'function') onGate(blocked); }, [onGate]);

  useEffect(() => {
    setAccepted(false); setReport(null); setError('');
    if (!isValidMint(mint)) { gate(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/safety/${mint.trim()}`);
        setReport(data);
        gate(data.risk_level === 'RED' || data.risk_level === 'ORANGE');
      } catch {
        setError('Safety check temporarily unavailable.');
        gate(false); // never block trading on a scan failure
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [mint, gate]);

  const accept = async () => {
    setAccepted(true);
    gate(false);
    try {
      await axios.post(`${API_BASE}/api/safety/ack`, {
        mint: report?.mint, score: report?.risk_score, level: report?.risk_level,
        source, actor, disclaimer: DISCLAIMER,
      });
    } catch { /* proceed even if logging fails */ }
  };

  if (!isValidMint(mint)) return null;

  if (loading) return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/60 border border-slate-700 text-xs text-slate-400 font-mono">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning token safety…
    </div>
  );
  if (error) return (
    <div className="p-2 rounded-lg bg-slate-800/60 border border-slate-700 text-xs text-slate-500 font-mono">
      {error} Trading not blocked.
    </div>
  );
  if (!report) return null;

  const s = LEVEL[report.risk_level] || LEVEL.YELLOW;
  const danger = report.risk_level === 'RED' || report.risk_level === 'ORANGE';

  return (
    <div className={`rounded-lg border ${s.ring} ${s.bg} p-3 space-y-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {report.risk_level === 'GREEN'
            ? <ShieldCheck className="w-4 h-4" style={{ color: s.color }} />
            : <ShieldAlert className="w-4 h-4" style={{ color: s.color }} />}
          <span className="text-xs font-mono font-bold" style={{ color: s.color }}>{report.risk_level}</span>
          <span className="text-[10px] font-mono text-slate-400">· {report.risk_score}/100 risk</span>
        </div>
        <button onClick={() => setExpanded(p => !p)} className="text-slate-400 hover:text-white">
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <p className="text-[11px] text-slate-300 font-mono">{report.verdict}</p>

      {expanded && (
        <div className="space-y-1 pt-1 border-t border-slate-700/60">
          {(report.signals || []).map(sig => (
            <div key={sig.name} className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: sig.triggered ? '#ef4444' : '#10B981' }} />
              <div>
                <p className="text-[10px] font-mono text-slate-300 uppercase">{sig.name.replace(/_/g, ' ')}</p>
                <p className="text-[10px] text-slate-500">{sig.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {danger && !accepted && (
        <div className="mt-2 p-2 rounded-md border border-red-500/40 bg-red-500/5 space-y-2">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
            <span className="text-[11px] font-mono font-bold text-red-400">Risk acknowledgment required</span>
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed">{DISCLAIMER}</p>
          <button onClick={accept}
            className="w-full text-[11px] font-mono font-bold py-1.5 rounded-md bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 transition-colors">
            I understand and accept the risk — proceed
          </button>
        </div>
      )}
      {danger && accepted && (
        <p className="text-[10px] font-mono text-slate-500">Risk acknowledged and logged.</p>
      )}
    </div>
  );
}
